import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Send, Mail, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin control card for the weekly merchant digest email.
 * - Toggle on/off
 * - "Run Now" button (with optional `only_email` for sample previews)
 * - Shows last-run summary
 */
export function MerchantDigestCard({ api }) {
  const [cfg, setCfg] = useState({ enabled: false });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [sampleEmail, setSampleEmail] = useState('');

  const fetchCfg = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/merchant-digest/config');
      setCfg(data);
    } catch (e) { /* non-fatal */ }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchCfg(); }, [fetchCfg]);

  const toggle = async (next) => {
    try {
      const { data } = await api.put('/api/admin/merchant-digest/config', { enabled: next });
      setCfg(data);
      toast.success(next ? 'Weekly digests scheduled' : 'Weekly digests paused');
    } catch (e) {
      toast.error('Failed to toggle');
    }
  };

  const runNow = async () => {
    if (sampleEmail && !sampleEmail.includes('@')) {
      toast.error('Enter a valid email or leave the field blank to send to all merchants');
      return;
    }
    if (!sampleEmail && !window.confirm(
      `Send today's digest batch now (up to ${cfg.today?.remaining ?? 0} more today) to merchants who haven't received this week's digest?`
    )) return;

    setRunning(true);
    try {
      const { data } = await api.post('/api/admin/merchant-digest/run',
        sampleEmail ? { only_email: sampleEmail } : {});
      toast.success(`Done — sent ${data.sent}, failed ${data.failed}, skipped ${data.skipped_no_activity}${data.daily_cap_reached ? ' · daily limit reached, continues tomorrow' : ''}`);
      await fetchCfg();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="bg-[#041A3B] border-white/10" data-testid="merchant-digest-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#00E5FF]" /> Weekly Merchant Digest
            </CardTitle>
            <CardDescription className="text-[#A0B4CC]">
              Auto-sends each approved merchant a weekly summary (bookings, revenue, top services, views).
              Uses the editable <code className="text-[#00E5FF]">merchant_weekly_digest</code> template.
            </CardDescription>
          </div>
          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide
            ${cfg.enabled ? 'bg-[#00E676]/15 text-[#00E676]' : 'bg-white/5 text-[#5C7599]'}`}>
            {cfg.enabled ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
            {cfg.enabled ? 'Enabled' : 'Paused'}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-[#A0B4CC] text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#000B18]">
              <div>
                <Label className="text-white">Auto-send weekly in daily batches</Label>
                <p className="text-[#5C7599] text-xs mt-0.5">
                  Each week (Mon–Sun) every merchant gets one digest, sent in daily batches from 9:00 AM Manila time (shares the {cfg.today?.cap ?? 200}/day reminder limit with onboarding follow-ups) until everyone is reached. Merchants with no activity are skipped.
                </p>
              </div>
              <Switch
                checked={!!cfg.enabled}
                onCheckedChange={toggle}
                data-testid="digest-enable-switch"
              />
            </div>

            <div className="p-3 rounded-lg bg-[#000B18] space-y-2">
              <Label className="text-white text-sm">Run digest manually</Label>
              <p className="text-[#5C7599] text-xs">
                Sends today's remaining batch to merchants who haven't received this week's digest yet. Or enter a single email to preview the digest for that merchant only.
              </p>
              <div className="flex gap-2 flex-wrap">
                <Input
                  value={sampleEmail}
                  onChange={(e) => setSampleEmail(e.target.value)}
                  placeholder="(optional) preview email — leave blank for all"
                  className="bg-[#020812] border-white/20 text-white flex-1 min-w-[200px]"
                  data-testid="digest-sample-email"
                />
                <Button onClick={runNow} disabled={running}
                  className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90"
                  data-testid="digest-run-now">
                  {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Run Now</>}
                </Button>
              </div>
            </div>

            {cfg.cycle && (
              <div className="grid grid-cols-3 gap-2 text-center" data-testid="digest-batch-progress">
                <div className="p-3 rounded-lg bg-[#000B18]">
                  <p className="text-[10px] uppercase tracking-wide text-[#5C7599]">Sent today</p>
                  <p className="text-white font-semibold" data-testid="digest-today-sent">{cfg.today?.sent ?? 0} / {cfg.today?.cap ?? 200}</p>
                </div>
                <div className="p-3 rounded-lg bg-[#000B18]">
                  <p className="text-[10px] uppercase tracking-wide text-[#5C7599]">Received this week</p>
                  <p className="text-[#00E676] font-semibold" data-testid="digest-cycle-received">{cfg.cycle.received}</p>
                </div>
                <div className="p-3 rounded-lg bg-[#000B18]">
                  <p className="text-[10px] uppercase tracking-wide text-[#5C7599]">Still to send</p>
                  <p className="text-[#FFEA00] font-semibold" data-testid="digest-cycle-pending">{cfg.cycle.pending} of {cfg.cycle.total}</p>
                </div>
              </div>
            )}

            {cfg.last_run && (
              <div className="text-xs text-[#A0B4CC] p-3 rounded-lg bg-[#000B18]">
                <p className="text-white font-semibold mb-1">Last run:</p>
                <p>{cfg.last_run.ran_at?.slice(0, 19).replace('T', ' ')} — sent <strong className="text-[#00E676]">{cfg.last_run.sent}</strong>, failed <strong className="text-[#FF1744]">{cfg.last_run.failed}</strong>, skipped <strong>{cfg.last_run.skipped_no_activity}</strong></p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
